import { useQueryClient } from '@tanstack/react-query';
import { Dispatch, SetStateAction, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useLaborQueryKeys from '../labor/requests/queryKeys';
import ContextProviderProps from '../models/ContextProviderProps';
import siteSort from '../utils/siteUtils/siteSort';
import { useUsers } from './userContext';
import { organizationViewSite } from '../admin/SiteSelector/SiteSelector';
import { isAllSitesRoute, isExcludeSiteUpdateRoute } from '../admin/utils/siteUtils';

// needs to update in shared library if everything is implemented
export type SiteInfo = {
  enterpriseUnitId: string;
  name?: string;
  referenceId?: string;
  timeZone?: string;
  id?: string;
  bslSiteId?: string;
};

const defaultSite: SiteInfo = {
  enterpriseUnitId: '',
  name: '',
  referenceId: '',
  timeZone: '',
};

export type TSiteContext = {
  sortedSites: SiteInfo[];
  selectedSite: SiteInfo;
  updateSelectedSite: (site: SiteInfo) => void;
  multiSelectedSites: SiteInfo[];
  updateMultiSelectedSites: Dispatch<SetStateAction<SiteInfo[]>>;
  isSiteFiltering: boolean;
  updateIsSiteFiltering: Dispatch<SetStateAction<boolean>>;
  lastSelectedSite: SiteInfo;
};

export const SitesContext = createContext<TSiteContext>({
  sortedSites: [],
  selectedSite: defaultSite,
  updateSelectedSite: (site) => {
    defaultSite.name = site.name;
    defaultSite.enterpriseUnitId = site.enterpriseUnitId;
    defaultSite.referenceId = site.referenceId;
    defaultSite.timeZone = site.timeZone;
  },
  multiSelectedSites: [],
  updateMultiSelectedSites: (_sites) => null,
  isSiteFiltering: false,
  updateIsSiteFiltering: () => null,
  lastSelectedSite: defaultSite,
});

export const useSites = () => useContext(SitesContext);

export function SitesContextProvider({ children }: ContextProviderProps) {
  const history = useHistory();
  const users = useUsers();
  const location = useLocation();
  // Feature flag to enable Site Switcher from BSL common component
  const [sortedSites, setSortedSites] = useState<SiteInfo[]>([defaultSite]);
  const [selectedSite, setSelectedSite] = useState<SiteInfo>(defaultSite);
  const [multiSelectedSites, updateMultiSelectedSites] = useState<SiteInfo[]>([]);
  const [isSiteFiltering, updateIsSiteFiltering] = useState<boolean>(false);
  const [lastSelectedSite, setLastSelectedSite] = useState<SiteInfo>(defaultSite);

  const queryClient = useQueryClient();

  // Helper function to generate localStorage key
  const getStorageKey = useCallback(() => `${users.id}-${users.org?.bslId}-selectedSite`, [users.id, users.org?.bslId]);

  // Helper function to handle localStorage operations
  const updateSiteInStorage = useCallback(
    (siteData: SiteInfo) => {
      if (users.id && users.org?.bslId) {
        localStorage.setItem(getStorageKey(), JSON.stringify(siteData));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users.id, users.org?.bslId]
  );
  const updateSelectedSite = useCallback(
    (site: SiteInfo) => {
      if (!site || !users.org?.bslId || users.isOrgSwitching) {
        return;
      }

      const siteName = site.name?.toLowerCase();
      const orgViewName = organizationViewSite.name?.toLowerCase();
      const isOrgViewSite = siteName === orgViewName;

      // Handle organization view site selection
      if (isOrgViewSite) {
        const currentSite = localStorage.getItem(getStorageKey());
        const lastSite = currentSite ? JSON.parse(currentSite) : defaultSite;

        if (lastSite.name?.toLowerCase() !== orgViewName) {
          setLastSelectedSite(lastSite);
          updateSiteInStorage({ ...lastSite, bslSiteId: site.id || site.enterpriseUnitId });
        }
      }

      // Update site selection state
      setSelectedSite(site);
      updateMultiSelectedSites([site]);

      // Save non-default, non-organization sites to localStorage
      if (site !== defaultSite && !isOrgViewSite) {
        updateSiteInStorage(site);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users.id, users.org?.bslId, users.isOrgSwitching]
  );

  const querykey = useLaborQueryKeys().employee.listByFilters({ enterpriseUnit: selectedSite.enterpriseUnitId });
  useEffect(() => {
    // we need to invalidate the employees api
    queryClient.invalidateQueries({
      queryKey: querykey,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [querykey]);
  const providerValue = useMemo(
    () => ({
      sortedSites,
      selectedSite,
      updateSelectedSite,
      multiSelectedSites,
      updateMultiSelectedSites,
      isSiteFiltering,
      updateIsSiteFiltering,
      lastSelectedSite,
    }),
    [sortedSites, selectedSite, updateSelectedSite, multiSelectedSites, isSiteFiltering, lastSelectedSite]
  );

  const resetSiteStateForOrgSwitch = () => {
    updateSelectedSite(defaultSite);
    setLastSelectedSite(defaultSite);
  };

  const restoreSelectedSiteAndUpdateUrl = (siteData: SiteInfo) => {
    if (
      siteData.enterpriseUnitId !== selectedSite.enterpriseUnitId &&
      users.bslAuth?.sites &&
      Object.values(users.bslAuth.sites).some((site) => site.enterpriseUnitId === siteData.enterpriseUnitId)
    ) {
      const existingSearchParams = new URLSearchParams(location.search);
      if (users.org?.bslLongName) {
        existingSearchParams.set('organizationId', users.org.bslLongName);
      }
      existingSearchParams.set('siteId', siteData.enterpriseUnitId ?? '');
      history.replace({ search: existingSearchParams.toString() });
      updateSelectedSite(siteData);
    }
  };

  useEffect(() => {
    const currentSite = localStorage.getItem(getStorageKey());
    const siteData: SiteInfo = currentSite ? JSON.parse(currentSite) : undefined;
    if (users.isOrgSwitching) {
      resetSiteStateForOrgSwitch();
    }
    if (users.bslAuth?.sites && !users.isOrgSwitching) {
      setSortedSites(siteSort(Object.values(users.bslAuth.sites)));
      const shouldSkipOrgViewSiteUpdate =
        isAllSitesRoute(location.pathname) || isExcludeSiteUpdateRoute(location.pathname);
      if (
        siteData &&
        (siteData.name === organizationViewSite.name || siteData?.bslSiteId === organizationViewSite.id) &&
        shouldSkipOrgViewSiteUpdate
      )
        return undefined;
      if (siteData && siteData.enterpriseUnitId !== defaultSite.enterpriseUnitId) {
        restoreSelectedSiteAndUpdateUrl(siteData);
      } else {
        const sortedSitesArray = siteSort(Object.values(users.bslAuth.sites));
        if (sortedSitesArray.length > 0) {
          updateSelectedSite(sortedSitesArray[0]);
        }
      }
    }

    return () => {
      if (
        siteData &&
        siteData.name === organizationViewSite.name &&
        process.env.REACT_APP_USE_PREVIEW_ORG_SWITCHER === 'true'
      ) {
        localStorage.setItem(`${users.id}-${users.org?.bslId}-selectedSite`, JSON.stringify(defaultSite));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users.bslAuth?.sites, users.id, users.org, users.org?.bslId, users.isOrgSwitching]);

  return <SitesContext.Provider value={providerValue}>{children}</SitesContext.Provider>;
}

export default SitesContextProvider;
